import { Button, cn } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { useHotkeys as useModifierHotkeys } from 'react-hotkeys-hook'
import { Input } from '#src/components/core/input/Input.tsx'
import { useHotkeysStore } from '#src/hooks/useHotkeys.ts'
import { useSearchFocusRequestStore } from '#src/hooks/useSearchFocusRequestStore.ts'

// Advertises the focus shortcut. There's no shared platform-aware key-display
// util in the codebase, so we show the macOS glyph per spec; `mod+…` still maps
// to Ctrl on Windows/Linux regardless of the label.
const DEFAULT_SHORTCUT = 'mod+f'
const DEFAULT_SHORTCUT_LABEL = '⌘F'

interface ShortcutSearchInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  /* Focus combo in react-hotkeys-hook syntax. Defaults to `mod+f` for the
   * visit notes page; the desk list view passes `mod+shift+f` so it doesn't
   * take the browser's find on a surface that isn't a document. */
  shortcut?: string
  /* How that combo is written in the default placeholder. */
  shortcutLabel?: string
  /* Shows a clear button inside the field's right edge while `value` is
   * non-empty. Opt-in, and it is the wrapper below that makes it opt-in: every
   * other surface keeps rendering a bare input, so none of their rows have a
   * new box in them. */
  clearable?: boolean
  /* Layout classes for that wrapper. `className` keeps going to the input, so a
   * call site's height/border/shadow classes land where they always did; only
   * the classes that size the field within its row (`flex-1`, `min-w-0`) have
   * to move out here, since the wrapper is what the row now lays out. */
  containerClassName?: string
}

// Search input with page-scoped Cmd/Ctrl+F focus and a blue outline shown ONLY
// when focused via that shortcut — pointer/tab focus keeps the box's normal,
// ring-suppressed styling. The hotkey attaches on mount and detaches on unmount,
// so the browser's native find is preserved on pages that don't render this.
export function ShortcutSearchInput({
  value,
  onChange,
  placeholder,
  className,
  shortcut = DEFAULT_SHORTCUT,
  shortcutLabel = DEFAULT_SHORTCUT_LABEL,
  clearable = false,
  containerClassName,
}: ShortcutSearchInputProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  // True only while focused via the shortcut; this is what shows the blue
  // outline. Pointer/tab focus leaves it false (normal focus styling).
  const [shortcutFocused, setShortcutFocused] = useState(false)
  const hotkeysDisabled = useHotkeysStore((state) => state.disabled)

  const focusFromShortcut = useCallback(() => {
    // Mark the shortcut as the focus source so the blue outline shows for
    // Cmd/Ctrl+F only — pointer/tab focus clears it (onPointerDown/onBlur).
    setShortcutFocused(true)
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  // Registered through react-hotkeys-hook directly because the shared useHotkeys
  // wrapper suppresses single-letter hotkeys under a modifier (the 'f' filter
  // shortcut), which would swallow this combo. `mod` is Cmd on macOS, Ctrl else.
  // Attaches on mount and detaches on unmount, so the combo is scoped to
  // whichever surface renders this input.
  useModifierHotkeys(shortcut, focusFromShortcut, {
    enabled: !hotkeysDisabled,
    preventDefault: true,
    enableOnFormTags: ['input', 'select', 'textarea'],
    enableOnContentEditable: true,
  })

  /* A press of this same combo made on a surface that had no search field
   * mounted — see useSearchFocusRequestStore. Consuming it here rather than
   * exposing a focus prop is what lets the publisher stay ignorant of when this
   * input mounts, which on the desk is behind a lazy chunk and a room load.
   *
   * Inert everywhere until something publishes a request for this exact combo:
   * `requestedShortcut` is null on every surface that never does, so this runs
   * once on mount and stops. */
  const requestedShortcut = useSearchFocusRequestStore(
    (state) => state.requestedShortcut
  )
  const clearSearchFocusRequest = useSearchFocusRequestStore(
    (state) => state.clearSearchFocusRequest
  )
  const isFocusRequested = requestedShortcut === shortcut
  useEffect(() => {
    if (isFocusRequested) {
      focusFromShortcut()
    }
  }, [isFocusRequested, focusFromShortcut])

  /* Clearing is a request to retype, not to leave the field, so the caret ends
   * up back in the input. The component clears itself rather than taking an
   * `onClear` — `onChange` already owns the value and `inputRef` is here, which
   * is what lets every call site get the caret behavior for free. */
  const clearSearch = useCallback(() => {
    onChange('')
    inputRef.current?.focus()
  }, [onChange])

  /* Only while there is something to clear — an always-present X reads as a
   * control the field is missing a use for. The input's right padding is tied
   * to the same condition rather than reserved permanently: on a narrow pane
   * the placeholder needs every pixel, and it is what advertises ⌘⇧F. */
  const showClear = clearable && value !== ''

  const input = (
    <Input
      ref={inputRef}
      placeholder={placeholder ?? `Search (${shortcutLabel})`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onPointerDown={() => setShortcutFocused(false)}
      /* Released on the focus event, not when the effect above calls `focus()`:
       * this mount may be replaced before it settles, and only a request still
       * pending is picked up by the instance that replaces it. */
      onFocus={() => {
        if (isFocusRequested) {
          clearSearchFocusRequest()
        }
      }}
      onBlur={() => setShortcutFocused(false)}
      data-shortcut-active={shortcutFocused || undefined}
      className={cn(
        'focus-visible:ring-0 focus-visible:ring-offset-0',
        /* The placeholder advertises the combo with modifier symbols, and
         * DMSans has none of them — without this the ⇧ renders as a hairline
         * outline next to a normally-weighted ⌘. Scoped to ::placeholder so
         * the *typed* value keeps the global stack, whose CJK-before-platform
         * ordering is load-bearing for printing. */
        'placeholder:font-modifier-keys',
        // Blue outline only on the shortcut path; `!` overrides the suppression
        // above while the box is shortcut-focused.
        shortcutFocused && 'ring-1! ring-offset-0! ring-[#6d78d5]!',
        className,
        // Last, so it beats the variant's `px-3`: typed text must stop before
        // the clear button rather than run under it.
        showClear && 'pr-9'
      )}
    />
  )

  if (!clearable) {
    return input
  }

  return (
    <div className={cn('relative', containerClassName)}>
      {input}
      {showClear && (
        <Button
          type="button"
          variant="ghost"
          /* `size="unstyled"` because the smallest icon size is `size-9`, which
           * cannot sit inside a 40px field. The ghost variant is what keeps the
           * hover and focus-visible treatment the app's, rather than a second
           * idiom for a small round button. */
          size="unstyled"
          className="absolute top-1/2 right-1.5 size-6 -translate-y-1/2 rounded-full text-muted-foreground"
          aria-label="Clear search"
          /* Holds the caret in the field across the click. Without it the
           * button takes focus on mousedown and `clearSearch` has to hand it
           * back, which blurs and refocuses the input mid-click — and that blur
           * drops the shortcut outline on a field the caret never actually
           * left. The keyboard path still lands focus back through `onClick`. */
          onMouseDown={(e) => e.preventDefault()}
          onClick={clearSearch}
        >
          <IconX className="size-3.5" />
        </Button>
      )}
    </div>
  )
}
